'use client'

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { queryOptions, useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { useLocalStorage } from 'react-use'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { isLotUnmanifested } from '@b-stock/fe-core/bstock-next/helpers'
import { showSnackbar } from '@b-stock/fe-core/bstock-react'
import { Breakpoints } from '@b-stock/fe-core/design-system'

import { ManifestFullscreenModal } from '@/buyer-portal/components/features/listing/ListingManifestSection/ListingManifestAgGrid/ManifestFullscreenModal'
import {
  UnAuthWarningModalProvider,
  useUnAuthWarningModal,
} from '@/buyer-portal/components/features/UnAuthWarningModal'
import useAccountId from '@/buyer-portal/helpers/useAccountId'
import { useDownloadManifest } from '@/buyer-portal/helpers/useDownloadManifest'
import ManifestSummaryPopup from '@/components/listings/ManifestSummaryPopup'
import { useWatchlistToggler } from '@/components/listings/watchlist/useWatchlistToggler'
import WatchlistErrorToastContent from '@/components/listings/watchlist/WatchlistErrorToastContent'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import type { ListingDetails } from '@/queries/listingDetailsQuery'
import type { SearchResult } from '@/queries/listingSearchQuery'
import searchListingDetailsQuery, {
  getSearchListingDetails,
} from '@/queries/searchListingDetailsQuery'
import { allWatchedListingsQuery } from '@/queries/watchlistQuery'

import type { AnalyticsTracker } from '../../helpers/listViewAnalytics'
import type { ListViewSurface } from '../../helpers/listViewSurface'
import { createAiSummaryPanelAction } from '../actions/aiSummaryPanelAction'
import { createDownloadManifestPanelAction } from '../actions/downloadManifestPanelAction'
import { createViewManifestPanelAction } from '../actions/viewManifestPanelAction'
import {
  createWatchlistPanelAction,
  getWatchlistToggleTarget,
} from '../actions/watchlistPanelAction'
import { getActionPanelVariant } from '../getActionPanelVariant'
import type {
  PanelAction,
  PanelActionDefinition,
  PanelActionId,
} from '../types'
import {
  ActionPanelContext,
  type ActionPanelContextValue,
} from './ActionPanelContext'

/** One row's one action: what its pending state and status message are keyed by. */
const rowActionKey = (listingId: string, actionId: PanelActionId) =>
  `${listingId}/${actionId}`

/**
 * Where the buyer's collapse preference lives.
 *
 * One key for every List View — the ALP and every storefront alike — and
 * deliberately not keyed by pathname the way `useAgGridMemory` keys column
 * layout. Column choices are about one page's columns; this is one preference
 * about the panel, and a buyer who collapses it on the ALP means it collapsed.
 */
export const ACTION_PANEL_EXPANDED_KEY = 'BStock::ListView::actionPanelExpanded'

/** What a buyer who has never touched the chevron gets. */
const DEFAULT_IS_EXPANDED = true

/** How long a status message stays before dismissing itself. */
export const STATUS_MESSAGE_DURATION_MS = 5_000

const subscribeToBreakpoint = (onChange: () => void) => {
  const query = window.matchMedia(Breakpoints.max.medium)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

const getIsMobile = () => window.matchMedia(Breakpoints.max.medium).matches

/**
 * Desktop on the server. The grid mounts client-side, so this only decides the
 * value React hydrates against, and `wide` is the state the flag is about.
 */
const getIsMobileOnServer = () => false

/**
 * Provides everything the panel's cells share, resolved once for the whole grid.
 */
export const ActionPanelProvider = ({
  children,
  surface,
}: {
  children: ReactNode
  surface?: ListViewSurface
}) => (
  <UnAuthWarningModalProvider>
    <ActionRegistryProvider surface={surface}>
      {children}
    </ActionRegistryProvider>
  </UnAuthWarningModalProvider>
)

const ActionRegistryProvider = ({
  children,
  surface,
}: {
  children: ReactNode
  surface?: ListViewSurface
}) => {
  const { formatMessage } = useIntl()

  /**
   * Deliberately not `useIsMobileTableLayout`: that one returns `true` on its
   * first render by design, which would render every desktop grid `narrow` and
   * then widen it, in view of the buyer. Read synchronously instead, so the
   * first paint is already right.
   *
   * Live rather than read once at mount, because `variant` follows the viewport
   * even though the preference behind it does not. `useColumnPinning`'s
   * mount-time ref is still right for *pinning*, which must not slide in.
   */
  const isMobile = useSyncExternalStore(
    subscribeToBreakpoint,
    getIsMobile,
    getIsMobileOnServer
  )

  /**
   * Read synchronously at mount rather than applied in a layout effect, which
   * is what `FilterContainer`'s `useExpand` does. That effect exists to dodge a
   * hydration mismatch, and there is none to dodge here: `ListView` loads the
   * grid through `next/dynamic` with `ssr: false`, so nothing that reads this
   * ever renders on the server.
   *
   * Worth the difference, because the effect is one frame late: the grid would
   * mount showing the full form and then collapse — or, on mobile, mount with
   * the column and then lose it — in view of the buyer, on every page load.
   * Read up front, the first paint is already what they chose. Same reasoning
   * as `isMobile` above.
   *
   * No initial value is passed: react-use writes one straight back to storage,
   * which would record a preference the buyer never expressed.
   */
  const [storedIsExpanded, setIsExpanded] = useLocalStorage<boolean>(
    ACTION_PANEL_EXPANDED_KEY
  )
  const isExpanded = storedIsExpanded ?? DEFAULT_IS_EXPANDED

  /**
   * `useAnalytics()` returns a fresh object on every render, so it cannot be a
   * memo dependency without defeating every memo below — and an unstable
   * context value re-renders every visible row. Read it through a ref instead.
   */
  const { trackButtonClicked } = useAnalytics()
  const trackButtonClickedRef = useRef(trackButtonClicked)
  trackButtonClickedRef.current = trackButtonClicked // oxlint-disable-line react/refs

  const { openUnAuthModal } = useUnAuthWarningModal()
  const personId = useDecodedAccessToken()?.personId

  // [Perf] One request for the whole grid, not one per row.
  const { data: watchlistEntries } = allWatchedListingsQuery.useWithOpts(
    [personId ?? ''],
    { enabled: !!personId }
  )

  const watchedIds = useMemo(
    () => new Set((watchlistEntries ?? []).map((entry) => entry.listingId)),
    [watchlistEntries]
  )

  // Hearts the buyer has just clicked, held until the refetch agrees.
  const [pendingWatched, setPendingWatched] = useState<
    ReadonlyMap<string, boolean>
  >(new Map())

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setPendingWatched((current) => {
      if (current.size === 0) return current

      const settled = new Map(current)
      for (const [listingId, isWatched] of current) {
        if (watchedIds.has(listingId) === isWatched) settled.delete(listingId)
      }

      return settled.size === current.size ? current : settled
    })
  }, [watchedIds])

  const isWatched = useCallback(
    (listingId: string) =>
      pendingWatched.get(listingId) ?? watchedIds.has(listingId),
    [pendingWatched, watchedIds]
  )

  const setPending = useCallback(
    (listingId: string, isWatched: boolean | undefined) =>
      setPendingWatched((current) => {
        const next = new Map(current)
        if (isWatched === undefined) next.delete(listingId)
        else next.set(listingId, isWatched)
        return next
      }),
    []
  )

  const { toggleWatchlisting } = useWatchlistToggler()

  /** Listings with a toggle in flight, so a double-click cannot race itself. */
  const inFlightRef = useRef(new Set<string>())

  const toggleWatchlist = useCallback(
    (listing: CombinedListingSearchResult) => {
      const { listingId } = listing

      // A second click before the first settles would read the already-flipped
      // optimistic value and fire the opposite mutation against it.
      if (inFlightRef.current.has(listingId)) return

      const wasWatched = isWatched(listingId)

      let target
      try {
        // Before the heart moves, because deriving dateExpires from a malformed
        // endTime throws and a flip that happened first would stick with no
        // mutation behind it.
        target = getWatchlistToggleTarget(listing, wasWatched)
      } catch (error) {
        console.error('Error building the watchlist payload', error)
        showSnackbar({
          content: <WatchlistErrorToastContent isRemoving={wasWatched} />,
          options: {
            type: 'error',
            group: 'watchlist',
            autoClose: true,
            closeButton: true,
          },
        })
        return
      }

      inFlightRef.current.add(listingId)
      setPending(listingId, !wasWatched)

      void toggleWatchlisting({
        ...target,
        // A restore puts it back on the watchlist whatever it was before.
        onUndo: () => setPending(listingId, true),
      }).then((didSucceed) => {
        inFlightRef.current.delete(listingId)
        // Drop back to whatever the server says rather than guessing.
        if (!didSucceed) setPending(listingId, undefined)
      })
    },
    [isWatched, setPending, toggleWatchlisting]
  )

  // [Perf] One summary popup is shared by the whole grid. This is its data.
  const [summaryListing, setSummaryListing] = useState<SearchResult | null>(
    null
  )

  const openSummary = useCallback(
    (listing: CombinedListingSearchResult) =>
      setSummaryListing(listing.originalSearchResult),
    []
  )

  const closeSummary = useCallback(() => setSummaryListing(null), [])

  /*
   * Per row and action: the clicks still being handled, and the messages that
   * stood in for a result. Download Manifest is pending while the lot loads,
   * then either downloads and clears, or reads "No manifest for this lot."
   */
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set())
  const [statusMessages, setStatusMessages] = useState<
    ReadonlyMap<string, string>
  >(new Map())

  const setIsPending = useCallback((key: string, isPending: boolean) => {
    setPendingKeys((current) => {
      if (current.has(key) === isPending) return current
      const next = new Set(current)
      if (isPending) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  /*
   * Timers live here rather than in the icon: a virtualized row can unmount
   * while its message is showing, and the message should still go away.
   */
  const statusMessageTimers = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  )

  const setStatusMessage = useCallback(
    (key: string, message: string | null) => {
      clearTimeout(statusMessageTimers.current.get(key))
      if (message === null) {
        statusMessageTimers.current.delete(key)
      } else {
        statusMessageTimers.current.set(
          key,
          setTimeout(
            // oxlint-disable-next-line react/immutability
            () => setStatusMessage(key, null),
            STATUS_MESSAGE_DURATION_MS
          )
        )
      }

      setStatusMessages((current) => {
        const next = new Map(current)
        if (message === null) next.delete(key)
        else next.set(key, message)
        return next
      })
    },
    []
  )

  useEffect(
    () => () =>
      statusMessageTimers.current.forEach((timer) => clearTimeout(timer)),
    []
  )

  const accessToken = useAccessToken()
  const accountId = useAccountId()
  const queryClient = useQueryClient()

  /**
   * The row's full listing details, from the same query the bid form's postload
   * uses — so whichever of the two asked first, the other answers from cache.
   * A search row does not carry the lot's groups, which is what says whether it
   * is manifested, so a manifest action has to load these before it can act.
   */
  const detailsQueryOptions = useCallback(
    (listing: CombinedListingSearchResult) =>
      queryOptions({
        queryKey: searchListingDetailsQuery.getKey(accessToken, [
          listing,
          accountId,
        ]),
        queryFn: () => getSearchListingDetails(accessToken, listing, accountId),
      }),
    [accessToken, accountId]
  )

  const exportManifest = useDownloadManifest()

  /**
   * The sequence every manifest action shares: find out whether the lot is
   * manifested, showing the spinner while its details load, then do the
   * action's work or answer in the icon. The two answers — no manifest, and
   * failure — come from the action's own locale block.
   */
  const runManifestAction = useCallback(
    (
      listing: CombinedListingSearchResult,
      actionId: 'downloadManifest' | 'viewManifest',
      act: (details: ListingDetails) => Promise<void> | void
    ) => {
      const key = rowActionKey(listing.listingId, actionId)
      const ns = `Listings.ListView.actionPanel.${actionId}`
      const answerNoManifest = () =>
        setStatusMessage(key, formatMessage({ id: `${ns}.noManifest` }))

      // Answered without a request, so without a spinner: no lot to ask about,
      // or details already loaded that say the lot is unmanifested. A row with
      // no lot gets the same answer as an unmanifested lot: one behavior for
      // what looks, to the buyer, like one situation.
      const options = detailsQueryOptions(listing)
      const cached = queryClient.getQueryData(options.queryKey)
      if (!listing.lotId || (cached && isLotUnmanifested(cached.lot))) {
        answerNoManifest()
        return
      }

      // Pending only while there is something to wait for — the load, or work
      // that is itself asynchronous — so a cached row that opens the viewer
      // never shows the spinner at all.
      void (async () => {
        try {
          let details = cached
          if (!details) {
            setIsPending(key, true)
            details = await queryClient.query({
              ...options,
              staleTime: 'static',
            })
          }

          if (isLotUnmanifested(details.lot)) {
            answerNoManifest()
            return
          }

          const work = act(details)
          if (work) {
            setIsPending(key, true)
            await work
          }
        } catch (error) {
          console.error(`Error running ${actionId}`, error)
          showSnackbar({
            content: formatMessage({ id: `${ns}.errorText` }),
            options: {
              type: 'error',
              group: 'manifest',
              autoClose: true,
              closeButton: true,
            },
          })
        } finally {
          setIsPending(key, false)
        }
      })()
    },
    [
      detailsQueryOptions,
      formatMessage,
      queryClient,
      setIsPending,
      setStatusMessage,
    ]
  )

  const downloadManifest = useCallback(
    (listing: CombinedListingSearchResult) =>
      runManifestAction(listing, 'downloadManifest', (details) =>
        exportManifest(details.lot._id, listing.title)
      ),
    [exportManifest, runManifestAction]
  )

  // [Perf] One fullscreen viewer is shared by the whole grid. This is its data.
  const [manifestListing, setManifestListing] = useState<ListingDetails | null>(
    null
  )

  const viewManifest = useCallback(
    (listing: CombinedListingSearchResult) =>
      runManifestAction(listing, 'viewManifest', setManifestListing),
    [runManifestAction]
  )

  const closeManifest = useCallback(() => setManifestListing(null), [])

  const definitions = useMemo<PanelActionDefinition[]>(() => {
    const tracker: AnalyticsTracker = {
      trackButtonClicked: (data) => trackButtonClickedRef.current(data),
    }

    /*
     * The registry. The panel's width follows its length. Sorted rather than
     * listed in order, so the sequence is the declared `order`.
     */
    const registered: PanelActionDefinition[] = [
      // oxlint-disable-next-line react/refs
      createWatchlistPanelAction({
        formatMessage,
        surface,
        tracker,
        isSignedIn: !!personId,
        isWatched,
        openUnAuthModal,
        toggleWatchlist,
      }),
      // oxlint-disable-next-line react/refs
      createViewManifestPanelAction({
        formatMessage,
        surface,
        tracker,
        isSignedIn: !!personId,
        openUnAuthModal,
        viewManifest,
      }),
      // oxlint-disable-next-line react/refs
      createDownloadManifestPanelAction({
        formatMessage,
        surface,
        tracker,
        isSignedIn: !!personId,
        openUnAuthModal,
        downloadManifest,
      }),
      // oxlint-disable-next-line react/refs
      createAiSummaryPanelAction({
        formatMessage,
        surface,
        tracker,
        openSummary,
      }),
    ]

    return registered.sort((a, b) => a.order - b.order)
  }, [
    downloadManifest,
    formatMessage,
    isWatched,
    openSummary,
    openUnAuthModal,
    personId,
    surface,
    toggleWatchlist,
    viewManifest,
  ])

  const getActions = useCallback(
    (listing: CombinedListingSearchResult): PanelAction[] =>
      definitions.map(
        ({ description, icon, isAvailable, onClick, ...definition }) => {
          const key = rowActionKey(listing.listingId, definition.id)
          return {
            ...definition,
            description:
              typeof description === 'function'
                ? description(listing)
                : description,
            dismissStatusMessage: () => setStatusMessage(key, null),
            icon: typeof icon === 'function' ? icon(listing) : icon,
            isAvailable: isAvailable?.(listing) ?? true,
            isPending: pendingKeys.has(key),
            onClick: () => onClick(listing),
            statusMessage: statusMessages.get(key) ?? null,
          }
        }
      ),
    [definitions, pendingKeys, setStatusMessage, statusMessages]
  )

  const value = useMemo<ActionPanelContextValue>(
    () => ({
      actionCount: definitions.length,
      getActions,
      isExpanded,
      setIsExpanded,
      surface,
      variant: getActionPanelVariant({ isExpanded, isMobile }),
    }),
    [
      definitions.length,
      getActions,
      isExpanded,
      isMobile,
      setIsExpanded,
      surface,
    ]
  )

  return (
    <ActionPanelContext.Provider value={value}>
      {children}
      {summaryListing && (
        <ManifestSummaryPopup listing={summaryListing} onClose={closeSummary} />
      )}
      {manifestListing && (
        <ManifestFullscreenModal
          listing={manifestListing}
          onClose={closeManifest}
        />
      )}
    </ActionPanelContext.Provider>
  )
}
